/**
 * WO#4B — Territory Identity domain types + pure Display Name validation.
 *
 * The persistent Territory Identity id is a backend-generated immutable id.
 * The membership fingerprint is authored by the backend and only ECHOED by the
 * client on SAVE (no client-side recomputation → no cross-language drift).
 */

export type TerritoryResolution =
  | "unchanged"
  | "expansion"
  | "new"
  | "reconciliation_required"
  | "reconciled";

export interface ReconciliationCandidate {
  identity_id: string;
  display_name: string;
  revision: number;
  binding: {
    member_parcel_ids: number[];
    unit_count: number;
    membership_fingerprint: string;
    as_of_block: number;
  };
}

export interface ResolvedTerritory {
  owner: string;
  member_parcel_ids: number[];
  membership_fingerprint: string;
  area: number;
  publishable: boolean;
  unit_count: number;
  as_of_block: number;
}

export interface TerritoryIdentityRecord {
  id: string;
  owner: string;
  display_name: string;
  revision: number;
  status: string;
  binding: {
    member_parcel_ids: number[];
    membership_fingerprint: string;
    as_of_block: number;
  };
}

export interface ResolveResponse {
  seed_parcel_id: number;
  territory: ResolvedTerritory;
  identity: TerritoryIdentityRecord | null;
  resolution: TerritoryResolution;
  reconciliation: { candidates: ReconciliationCandidate[] } | null;
}

export interface SaveResponse {
  identity: TerritoryIdentityRecord;
  territory: ResolvedTerritory;
  resolution: TerritoryResolution;
}

export interface ReconcileResponse {
  identity: TerritoryIdentityRecord;
  territory: ResolvedTerritory;
  resolution: TerritoryResolution;
}

export const DISPLAY_NAME_MIN = 1;
export const DISPLAY_NAME_MAX = 64;

export type DisplayNameValidation =
  | { ok: true; value: string }
  | { ok: false; reason: string };

/**
 * Mirror of the backend rule (WO#4B §3): 1–64 chars after trim; no newline /
 * control characters. Duplicate names allowed; no uniqueness.
 */
export function validateDisplayName(raw: string): DisplayNameValidation {
  const value = raw.trim();
  if (value.length < DISPLAY_NAME_MIN || value.length > DISPLAY_NAME_MAX) {
    return { ok: false, reason: `Name must be ${DISPLAY_NAME_MIN}–${DISPLAY_NAME_MAX} characters.` };
  }
  for (const ch of value) {
    const o = ch.codePointAt(0) ?? 0;
    if (ch === "\n" || ch === "\r" || o < 0x20 || o === 0x7f) {
      return { ok: false, reason: "Name must not contain control characters." };
    }
  }
  return { ok: true, value };
}
